/**
 * Content attachment type definitions that closely match Langston schema:
 * @link https://newsela.com/langston/#/content/content_attachments_list
 */

import { GradeBandName } from "@/constants/GradeAndLexileLevels";
import { LANG } from "@/constants/Language/Language";
import { ContentLevel } from "./Content.interface";

export enum ATTACHMENT_CONTENT_TYPE {
  CHECKS_FOR_UNDERSTANDING_QUIZ = "CHECKS_FOR_UNDERSTANDING_QUIZ",
  WRITE_PROMPT = "WRITE_PROMPT",
  QUIZ = "QUIZ",
  POWER_WORDS_ACTIVITY = "POWER_WORDS_ACTIVITY",
  FORMATIVE = "FORMATIVE",
}

export enum ASSESSMENT_TYPE {
  ANNOTATIONS = "ANNOTATIONS",
  CHECKS_FOR_UNDERSTANDING_QUIZ = "CHECKS_FOR_UNDERSTANDING_QUIZ",
  GRAPHIC_ORGANIZERS = "GRAPHIC_ORGANIZERS",
  GUIDED_HIGHLIGHTING_QUIZ = "GUIDED_HIGHLIGHTING_QUIZ",
  WRITE_PROMPT = "WRITE_PROMPT",
  QUIZ = "QUIZ",
  POLL = "POLL",
  POWER_WORDS_ACTIVITY = "POWER_WORDS_ACTIVITY",
}

export enum ATTACHMENT_QUESTION_TYPE {
  WRITE_PROMPT = "WRITE_PROMPT",
  QUIZ = "QUIZ",
}

export enum ATTACHMENT_DATA_STATUS {
  published = "PUBLISHED",
}

export enum GENRE {
  FICTION = "FICTION",
  NONFICTION = "NONFICTION",
}

interface INode {
  text?: string;
  type: string;
  content?: INode[];
}

export interface AttachmentQuestion {
  id: string;
  options: QuizOption[];
  position: number;
  question: string;
  update_at: string;
  raw_question: INode;
  html_question: string;
  question_type: ATTACHMENT_QUESTION_TYPE;
}

export interface AttachmentSubject {
  content_type: string;
  data: {
    custom: boolean;
    name: string;
    slug: string;
    subject: string;
  };
  id: string;
  is_visible: boolean;
}

export interface QuizOption {
  id: string;
  is_correct: boolean;
  html_text: string;
  raw_text: INode;
  position: number;
  text: string;
}
export interface QuizQuestion extends AttachmentQuestion {
  genre: GENRE;
  options: QuizOption[];
}

export interface PowerWordQuestion extends AttachmentQuestion {
  genre: GENRE;
  word_definition: {
    id: string;
  };
}

export interface AttachmentLevel extends ContentLevel {
  id: string;
  language: LANG;
  is_active: boolean;
  questions: AttachmentQuestion;
  grade_band: GradeBandName;
}

export interface Attachment {
  name: string;
  levels: AttachmentLevel[];
  status: ATTACHMENT_DATA_STATUS;
  thumbnail?: string;
  content_type: ATTACHMENT_CONTENT_TYPE;
  assessment_type: ASSESSMENT_TYPE;
}

export interface AttachmentWrapper {
  id: string;
  created_date: string;
  modified_date: string;
  content_type: ATTACHMENT_CONTENT_TYPE;
  data: Attachment;
}

export interface WritePrompt extends Attachment {
  content_type: ATTACHMENT_CONTENT_TYPE.WRITE_PROMPT;
  assessment_type: ASSESSMENT_TYPE.WRITE_PROMPT;
  subject: AttachmentSubject;
}

export interface Quiz extends Attachment {
  content_type: ATTACHMENT_CONTENT_TYPE.QUIZ;
  assessment_type: ASSESSMENT_TYPE.QUIZ;
  subject: AttachmentSubject;
}

export interface PowerWordsActivity extends Attachment {
  content_type: ATTACHMENT_CONTENT_TYPE.POWER_WORDS_ACTIVITY;
  assessment_type: ASSESSMENT_TYPE.POWER_WORDS_ACTIVITY;
}
